import { CommonModule } from '@angular/common';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  input,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DraggableContainerComponent,
  DraggableItem,
  QaiAccordianPanelComponent,
  QaiBadgeComponent,
  QaiButtonComponent,
  QaiFloatingDirective,
  QaiIconComponent,
  QaiLabelComponent,
  QaiTooltipComponent,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { MessagingThemeMessageComponent } from './messaging-theme-mesasge/messaging-theme-message.component';
import { MessagingThemeFlatFactComponent } from './messaging-theme-fact-flat/messaging-theme-fact-flat.component';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, Subscription } from 'rxjs';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';
import { ISectionsWithFacts } from 'src/app/interfaces/self-serve/requests/ISectionsWithFacts-update';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { IFact } from 'src/app/interfaces/self-serve/responses/ISectionsWithFacts';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { isComposingKeyEvent } from 'src/app/utils/keyboard-event.util';

@Component({
  selector: 'messaging-theme',
  standalone: true,
  templateUrl: './messaging-theme.component.html',
  styleUrls: ['./messaging-theme.component.scss'],
  imports: [
    ReactiveFormsModule,
    CommonModule,
    QaiButtonComponent,
    QaiAccordianPanelComponent,
    QaiIconComponent,
    QaiLabelComponent,
    DraggableContainerComponent,
    MessagingThemeMessageComponent,
    MessagingThemeFlatFactComponent,
    QaiBadgeComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class MessagingThemeComponent implements OnInit, OnDestroy, DoCheck {
  isSelfAuthoringRoleplay = input(false);

  @Input() sectionUuid: string | undefined = undefined;
  @Input() sectionName: string | undefined = undefined;
  @Input() messagingFactSection: FormGroup = this.fb.group({});
  @Input() messagingFactSectionFacts: FormArray<FormGroup> =
    this.fb.array<FormGroup>([]);
  @Input() otherThemeListIds: string[] = [];
  @Input() messagingFactSections!: FormArray;

  @Output() deleteRequested = new EventEmitter<string>();
  @Output() themeExpanded = new EventEmitter<string>();

  reviewSuggested = signal<boolean>(false);
  hasBlankFact = signal(false);

  @ViewChild('nameInput') nameInputRef?: ElementRef<HTMLInputElement>;
  callUuid: string = '';
  factsItems: DraggableItem[] = [];

  private factSubscriptions = new Map<string, Subscription>();
  private factsBlankSub?: Subscription;

  // Track initial values per fact
  private initialValues = new Map<string, string>();

  // Track if we're in the middle of adding a new fact
  private isAddingFact = false;

  // Track factsItems length to detect cross-list CDK drag (source list is mutated in-place, no event fires)
  private previousFactsItemsLength = -1;

  // Track if we're loading data from server
  private isLoadingFromServer = false;

  readOnly: boolean = false;
  defaultCorrectMessage: string = '';
  defaultIncorrectMessage: string = '';
  sectionNameControl = new FormControl('');
  sectionNameError: string = '';
  isEditingName: boolean = false;

  addThemeModalTitle: string = 'Update messaging theme';
  addThemeModalInputFieldText: string = 'Theme name';
  addThemeModalButtonText: string = 'Update';

  simulationStatus: string | null = null;

  constructor(
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private amplitudeService: AmplitudeService,
    private scoringRefinementService: ScoringRefinementService,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.simulationStatus = this.route.snapshot.queryParamMap.get('status');
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.sectionNameControl.setValue(this.sectionName || '');
    this.reviewSuggested.set(
      !SelfServeLocalStorageUtil.isThemeSectionReviewed(this.sectionUuid ?? ''),
    );
    this.loadFormFromProps();
  }

  ngOnDestroy(): void {
    this.factSubscriptions.forEach((sub) => sub?.unsubscribe());
    this.factSubscriptions.clear();
    this.factsBlankSub?.unsubscribe();
  }

  ngDoCheck(): void {
    const currentLength = this.factsItems.length;
    if (currentLength !== this.previousFactsItemsLength) {
      this.previousFactsItemsLength = currentLength;
      // CDK mutates factsItems in-place when a fact is dragged to another theme —
      // no event fires on the source, so we detect the divergence here.
      if (
        currentLength === 0 &&
        this.messagingFactSectionFacts.length > 0 &&
        !this.isAddingFact
      ) {
        while (this.messagingFactSectionFacts.length > 0) {
          this.messagingFactSectionFacts.removeAt(0, { emitEvent: false });
        }
        this.setupFactsValueChange();
        this.addNewMessage();
      }
    }
  }

  public loadFormFromProps(): void {
    this.isLoadingFromServer = true;
    this.initialValues.clear();

    (this.messagingFactSectionFacts.controls as FormGroup[]).forEach(
      (fact, index) => {
        const factUuid = fact.get('uuid')?.value;
        const key = factUuid || `index_${index}`;
        if (factUuid) {
          this.initialValues.set(key, JSON.stringify(fact.value));
        }
      },
    );

    this.syncFactsItems();
    this.setupFactsValueChange();
    this.watchBlankFact();

    setTimeout(() => {
      this.isLoadingFromServer = false;
    }, 1000);
  }

  private watchBlankFact(): void {
    this.factsBlankSub?.unsubscribe();
    this.factsBlankSub = this.messagingFactSectionFacts.valueChanges.subscribe(
      () => this.recomputeHasBlankFact(),
    );
    this.recomputeHasBlankFact();
  }

  private recomputeHasBlankFact(): void {
    this.hasBlankFact.set(
      this.messagingFactSectionFacts.controls.some(
        (fact) => !(fact.get('rawText')?.value ?? '').toString().trim(),
      ),
    );
  }

  addNewMessage() {
    this.isAddingFact = true;

    if (this.isSelfAuthoringRoleplay()) {
      this.amplitudeService.trackEvent(AmplitudeTrackingEvents.MESSAGING_FACT_ADDED, { theme: this.sectionTitle });
    }

    this.triggerSectionMessageSave(
      {
        factSections: [
          {
            uuid: this.sectionUuid!,
            name: this.sectionName!,
            facts: [
              {
                rawText: '',
                displayTextCorrect: this.defaultCorrectMessage,
                displayTextIncorrect: this.defaultIncorrectMessage,
                threshold: 7,
              },
            ],
          },
        ],
      },
      false,
    ); // fromOnChange = false
  }

  onExpandedChange(expanded: boolean): void {
    if (expanded && this.sectionUuid) {
      this.themeExpanded.emit(this.sectionUuid);
      this.reviewSuggested.set(false);
    }
  }

  deleteTheme(): void {
    if (this.isSelfAuthoringRoleplay()) {
      this.amplitudeService.trackEvent(AmplitudeTrackingEvents.MESSAGING_THEME_DELETED, { theme: this.sectionTitle });
    }
    if (this.sectionUuid) {
      this.deleteRequested.emit(this.sectionUuid);
    }
  }

  startEditing(): void {
    if (this.isSelfAuthoringRoleplay()) {
      this.amplitudeService.trackEvent(AmplitudeTrackingEvents.MESSAGING_THEME_EDITED, { theme: this.sectionTitle });
    }
    this.sectionNameControl.setValue(
      this.messagingFactSection?.get('name')?.value || this.sectionName || '',
    );
    this.isEditingName = true;
    setTimeout(() => {
      this.nameInputRef?.nativeElement?.focus();
      this.nameInputRef?.nativeElement?.select();
    });
  }

  handleNameInputKeydown(event: Event): void {
    if (isComposingKeyEvent(event as KeyboardEvent)) return;
    this.stopEditingName();
  }

  stopEditingName(): void {
    this.isEditingName = false;
    const newName = this.sectionNameControl.value?.trim() ?? '';
    const originalName = this.messagingFactSection?.get('name')?.value?.trim();
    const currentUuid = this.messagingFactSection?.get('uuid')?.value;

    if (!newName || newName === originalName) {
      this.sectionNameError = '';
      this.sectionNameControl.setValue(originalName, { emitEvent: false });
      return;
    }

    const nameExists = this.messagingFactSections?.value?.some(
      (section: any) =>
        section.uuid !== currentUuid && section.name?.trim() === newName,
    );

    if (nameExists) {
      this.sectionNameError = 'Theme with this name already exists.';
      this.sectionNameControl.setValue(originalName, { emitEvent: false });
      return;
    }

    // Patch optimistically, in the same tick as isEditingName flipping to
    // false, so the label (bound to sectionTitle) never re-renders with the
    // stale name — waiting for the save to resolve before patching left a
    // visible gap where the old name flashed before the new one appeared.
    this.sectionNameError = '';
    this.messagingFactSection.patchValue(
      { name: newName },
      { emitEvent: false },
    );

    console.log(
      `[rename] handleSectionUpdate — section: ${currentUuid}, new name: "${newName}"`,
    );
    this.authorService
      .updateSectionWithFacts(this.callUuid, {
        factSections: [{ uuid: currentUuid, name: newName, facts: [] }],
      })
      .subscribe({
        next: () => {
          if (this.isSelfAuthoringRoleplay()) {
            this.scoringRefinementService.notifySaved(this.callUuid);
          }
        },
        error: (err) => {
          console.error('Error updating section name:', err);
          // Roll back the optimistic patch since the save failed
          this.messagingFactSection.patchValue(
            { name: originalName },
            { emitEvent: false },
          );
          this.sectionNameControl.setValue(originalName, { emitEvent: false });
        },
      });
  }

  get sectionTitle(): string {
    const formName = this.messagingFactSection?.get('name')?.value;
    return formName || this.sectionName || 'My Message list';
  }

  private setupFactsValueChange() {
    this.factSubscriptions.forEach((sub) => sub?.unsubscribe());
    this.factSubscriptions.clear();

    this.messagingFactSectionFacts.controls.forEach((factGroup, index) => {
      this.addSubscriptionForFact(factGroup as FormGroup, index);
    });
  }

  private addSubscriptionForFact(factGroup: FormGroup, factIndex: number) {
    const factUuid = factGroup.get('uuid')?.value;
    const key = factUuid || `index_${factIndex}`;

    // Unsubscribe existing subscription for this key before creating a new one
    this.factSubscriptions.get(key)?.unsubscribe();

    // Seed initialValues for facts not yet tracked (e.g. dropped in from another theme)
    if (!this.initialValues.has(key)) {
      this.initialValues.set(key, JSON.stringify(factGroup.value));
    }

    const subscription = factGroup.valueChanges
      .pipe(debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE))
      .subscribe((updatedFact) => {
        if (this.isLoadingFromServer) return;

        if (this.isAddingFact && !this.initialValues.has(key)) return;

        const currentValue = JSON.stringify(updatedFact);
        const initialValue = this.initialValues.get(key);

        if (currentValue === initialValue) return;

        if (this.isSelfAuthoringRoleplay() && initialValue) {
          const previousFact = JSON.parse(initialValue);
          if (previousFact.rawText !== updatedFact.rawText) {
            this.amplitudeService.trackEvent(AmplitudeTrackingEvents.MESSAGING_FACT_EDITED, {
              field: 'messaging_fact',
              theme: this.sectionTitle,
            });
          }
          if (previousFact.displayTextIncorrect !== updatedFact.displayTextIncorrect) {
            this.amplitudeService.trackEvent(AmplitudeTrackingEvents.MESSAGING_FACT_EDITED, {
              field: 'display_text',
              theme: this.sectionTitle,
            });
          }
        }

        const factSections = [
          {
            uuid: this.messagingFactSection?.get('uuid')?.value!,
            name: this.messagingFactSection?.get('name')?.value!,
            facts: [updatedFact],
          },
        ];

        this.initialValues.set(key, currentValue);
        console.log(
          `[autosave] valueChanges fired — section: ${this.sectionUuid}, fact key: ${key}, index: ${factIndex}`,
        );
        this.triggerSectionMessageSave({ factSections }, true);
      });

    this.factSubscriptions.set(key, subscription);
  }

  private triggerSectionMessageSave(
    sections: ISectionsWithFacts,
    fromOnChange: boolean = false,
  ): void {
    if (this.simulationStatus === 'published') return;

    this.authorService
      .updateSectionWithFacts(this.callUuid, sections)
      ?.subscribe({
        next: (response) => {
          const updatedSection = response?.factSections?.[0].facts[0];
          if (!updatedSection?.uuid) return;

          this.addFactToFactSectionAndPatch(updatedSection, fromOnChange);

          if (fromOnChange && this.isSelfAuthoringRoleplay()) {
            this.scoringRefinementService.notifySaved(this.callUuid);
          }
        },
        error: (err) => {
          console.error('Failed to save section:', err);
          this.isAddingFact = false;
        },
      });
  }

  private addFactToFactSectionAndPatch(
    newFact: IFact,
    fromOnChange: boolean = false,
  ) {
    if (!this.messagingFactSection) {
      this.isAddingFact = false;
      return;
    }

    const key = newFact.uuid!;

    if (!fromOnChange) {
      const newFactGroup = this.fb.group({
        uuid: [newFact.uuid],
        rawText: [newFact.rawText],
        displayTextCorrect: [newFact.displayTextCorrect],
        displayTextIncorrect: [newFact.displayTextIncorrect],
        threshold: [newFact.threshold],
      });

      // Seed from FormGroup's value (not raw API response) so the autosave
      // comparison uses the exact same field set and doesn't fire a spurious call.
      this.initialValues.set(key, JSON.stringify(newFactGroup.value));

      this.messagingFactSectionFacts.push(newFactGroup);
      this.addSubscriptionForFact(
        newFactGroup,
        this.messagingFactSectionFacts.length - 1,
      );
      this.syncFactsItems();

      setTimeout(() => {
        this.isAddingFact = false;
      }, 50);
    }
  }

  onFactsReordered(items: DraggableItem[]): void {
    const reordered = items as unknown as FormGroup[];

    // Snapshot old order before rebuilding
    const oldControls =
      this.messagingFactSectionFacts.controls.slice() as FormGroup[];

    // Rebuild FormArray in new order without emitting
    const newControls = reordered.slice();
    while (this.messagingFactSectionFacts.length > 0) {
      this.messagingFactSectionFacts.removeAt(0, { emitEvent: false });
    }
    newControls.forEach((ctrl) =>
      this.messagingFactSectionFacts.push(ctrl, { emitEvent: false }),
    );
    this.syncFactsItems();
    this.setupFactsValueChange();

    // Find the first position where the order changed
    let firstChangedIndex = newControls.length;
    for (let i = 0; i < newControls.length; i++) {
      if (newControls[i] !== oldControls[i]) {
        firstChangedIndex = i;
        break;
      }
    }

    if (firstChangedIndex === newControls.length) return; // nothing actually moved

    // Only send facts from the first changed position onwards with their new factOrder
    const impactedFacts = newControls
      .slice(firstChangedIndex)
      .map((ctrl, offset) => ({
        ...ctrl.getRawValue(),
        factOrder: firstChangedIndex + offset + 1, // 1-based, >= 1
      }));

    console.log(
      `[reorder] onFactsReordered — section: ${this.sectionUuid}, impacted facts: ${impactedFacts.length}, from position: ${firstChangedIndex + 1}`,
      impactedFacts.map((f) => f['uuid']),
    );
    this.authorService
      .updateSectionWithFacts(this.callUuid, {
        factSections: [
          {
            uuid: this.messagingFactSection?.get('uuid')?.value,
            name: this.messagingFactSection?.get('name')?.value,
            facts: impactedFacts,
          },
        ],
      })
      ?.subscribe({
        next: () => {
          if (this.isSelfAuthoringRoleplay()) {
            this.scoringRefinementService.notifySaved(this.callUuid);
          }
        },
        error: (err) => console.error('Failed to save facts order:', err),
      });
  }

  deleteFact(index: number, factUuid: string) {
    this.authorService.deleteSectionFact(this.callUuid, factUuid)?.subscribe({
      next: () => {
        if (this.isSelfAuthoringRoleplay()) {
          this.amplitudeService.trackEvent(AmplitudeTrackingEvents.MESSAGING_FACT_DELETED, { theme: this.sectionTitle });
          this.scoringRefinementService.notifySaved(this.callUuid);
        }
        this.factSubscriptions.get(factUuid)?.unsubscribe();
        this.factSubscriptions.delete(factUuid);
        this.messagingFactSectionFacts.removeAt(index);
        this.initialValues.delete(factUuid);
        this.syncFactsItems();
      },
      error: (err) => console.error("Failed to delete section's fact:", err),
    });
  }

  private syncFactsItems(): void {
    this.factsItems =
      this.messagingFactSectionFacts.controls.slice() as unknown as DraggableItem[];
  }

  asFormGroup(item: DraggableItem): FormGroup {
    return item as unknown as FormGroup;
  }

  factIndexOf(item: DraggableItem): number {
    return this.messagingFactSectionFacts.controls.indexOf(
      item as unknown as FormGroup,
    );
  }
}
